import { Box, Checkbox, Stack, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';

import { BATCH_LABEL_IDS } from './types';
import type { BatchAllocationDetail, ReallocationBatchKey } from '../../types/response';

interface Step1SelectBatchProps {
  batches: BatchAllocationDetail[];
  selectedBatches: Set<ReallocationBatchKey>;
  onToggle: (batch: ReallocationBatchKey) => void;
  isLoading: boolean;
}

const BatchCard = ({
  batch,
  selected,
  onToggle,
}: {
  batch: BatchAllocationDetail;
  selected: boolean;
  onToggle: (batch: ReallocationBatchKey) => void;
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Box
      role="button"
      onClick={() => onToggle(batch.batch)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        px: theme.spacing(4.5),
        py: theme.spacing(5.5),
        borderRadius: 1,
        cursor: 'pointer',
        border: `${
          selected
            ? '2px solid ' + theme.palette.primary.main
            : '1px solid ' + theme.palette.divider
        }`,
        backgroundColor: selected ? theme.palette.action.hover : theme.palette.background.paper,
      }}
    >
      <Checkbox
        checked={selected}
        onChange={() => onToggle(batch.batch)}
        onClick={(e) => e.stopPropagation()}
        sx={{ p: 0, mt: '2px', mr: '20px' }}
        inputProps={{
          'aria-label': formatMessage({ id: BATCH_LABEL_IDS[batch.batch] }),
        }}
      />
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: BATCH_LABEL_IDS[batch.batch] })}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <Box component="span">
            {formatMessage(
              { id: 'adminProjects.reallocation.batch.humanAssignments' },
              { count: batch.totalHumanAllocated.toLocaleString() }
            )}
          </Box>
          {'  |  '}
          <Box component="span" sx={{ color: theme.palette.warning.main, fontWeight: 600 }}>
            {formatMessage(
              { id: 'adminProjects.reallocation.batch.pending' },
              { count: batch.pendingCount.toLocaleString() }
            )}
          </Box>
          {'  |  '}
          <Box component="span">
            {formatMessage(
              { id: 'adminProjects.reallocation.batch.draft' },
              { count: batch.draftCount.toLocaleString() }
            )}
          </Box>
          {'  |  '}
          <Box component="span">
            {formatMessage(
              { id: 'adminProjects.reallocation.batch.finalized' },
              { count: batch.finalisedCount.toLocaleString() }
            )}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export const Step1SelectBatch = ({
  batches,
  selectedBatches,
  onToggle,
  isLoading,
}: Step1SelectBatchProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {formatMessage({ id: 'adminProjects.reallocation.selectBatch.heading' })}
      </Typography>

      <Stack spacing={2}>
        {showLoadingSpinner && isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '216px',
            }}
          >
            <LoadingSpinner id="reallocationSelectBatchLoading" />
          </Box>
        ) : (
          batches.map((batch) => (
            <BatchCard
              key={batch.batch}
              batch={batch}
              selected={selectedBatches.has(batch.batch)}
              onToggle={onToggle}
            />
          ))
        )}
      </Stack>

      <Typography variant="body2" color={theme.palette.text.secondary}>
        {formatMessage({ id: 'adminProjects.reallocation.selectBatch.footnote' })}
      </Typography>
    </Box>
  );
};
